import parseDataToProps from './parseDataToProps';

export const getPageType = (pageCategory) => {
    const pageTypeMapping = {
        'home': 'home_page',
        'promotion_overview': 'promotion_page',
        'blog_overview': 'blog_page',
        'news_overview': 'news_page',
        'product_lister': 'category_page',
        'product_detail': 'product_page',
        'location_overview': 'location_page',
        'location_detail': 'location_page',
        '404': '404_page',
    };
    return pageTypeMapping[pageCategory] || 'content_page';
}

export const getPageLayout = () => {
    return window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
}

const parseStoredJson = (key, fallback) => {
    try {
        return JSON.parse(window.localStorage.getItem(key)) || fallback;
    } catch {
        return fallback;
    }
}

const getStoredInputValues = () => parseStoredJson('stored-input-values', {});

const getLastSeenProductItem = () => {
    const stored = parseStoredJson('last-seen-product-data', null);

    return stored?.identifier === window.localStorage.getItem('last-seen-product') ? stored.item : null;
}

export const pushPostalCodeDatalayer = async (finished, closed) => {
    window.dataLayer = window.dataLayer || [];

    let form_step = '1';
    let form_step_name = 'postcode invoeren';

    if (finished) {
        form_step = '2';
        form_step_name = 'postcode bevestigd';
    }

    if (closed) {
        form_step = '1';
        form_step_name = 'popup gesloten';
    }

    window.dataLayer.push({
        'event': 'form',
        'form_id': crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
        'form_name': 'postcode',
        'form_category': 'primary',
        'form_cta': 'postcode invoeren',
        'form_step': form_step,
        'form_step_name': form_step_name,
        'form_step_action': '',
        'form_type': 'overlay',
        'client_email': '',
        'form_subject_type': '',
    });

    if (!closed) {
        pushVirtualPageView('postal-code', form_step);
    }
}

export const pushDataLayer = (event, layerData = {}) => {
    window.dataLayer = window.dataLayer || [];
    const dataLayerNode = document.getElementById('datalayer-data');
    const { locale = '', pageCategory = '' } = parseDataToProps(dataLayerNode?.dataset || {});

    if (event === 'page') {
        layerData = {
            'page_type': getPageType(pageCategory),
            'page_category': pageCategory,
            'page_layout': getPageLayout(),
            'platform': 'web',
            'timestamp': new Date().toISOString(),
            'user_id': null,
            'webspace': 'stella',
            'country_code': locale.toLowerCase(),
            'country_language': locale.toLowerCase(),
            ...layerData,
        };
    }

    window.dataLayer.push({
        'event': event,
        ...layerData,
    });
}

export const pushEnhancedDatalayer = async () => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
        'customerEmail': getStoredInputValues().email || ''
    });
}

export const getConversionName = (conversionType) => {
    const conversionNames = {
        'testdrive': 'testride',
        'testcenter': 'appointment',
        'route': 'routeplanner',
        'postal-code': 'postal-code',
        'trade-in': 'trade-in'
    }
    return conversionNames[conversionType] || conversionType;
}

export const getCtaType = (ctaType) => {
    const ctaTypes = {
        'conversion_testdrive': 'conversion_testride',
        'conversion_testcenter': 'conversion_appointment',
        'conversion_route': 'conversion_routeplanner',
        'link': 'internal_link',
        'default': 'close_conversion',
    }
    return ctaTypes[ctaType] || ctaType;

}

export const getFormStepName = (step, steps, lastStep) => {
    if (typeof step !== 'number' || isNaN(step)) {
        return '';
    }
    if (step === 1) {
        return 'formulier geopend';
    } else if (step === steps.length) {
        return 'formulier voltooid';
    } else {
        return `stap ${step - 1} voltooid`;
    }
}

export const getFormStepAction = (conversionType) => {
    const stepActions = {
        'testdrive': 'testdrive',
        'testcenter': 'appointment',
        'route': 'routeplanner',
        'brochure': 'brochure',
        'trade-in': 'trade-in',
    };
    return stepActions[conversionType] || '';
}

export const getFormLayerData = (conversion, conversionType, step, steps, data = {}, location = '', ctaText = '') => {
    let lastStep = true;
    if (conversionType === 'testdrive' && (location === 'store' || location === 'stella')) {
        lastStep = false;
    }

    if (conversionType === 'testdrive') sessionStorage.setItem('lastTestdriveStep', step);

    const formCta = ctaText || (step === steps.length && conversion.link_last_step?.length > 0 && lastStep
        ? conversion.link_last_step[0]?.link_title || ''
        : '');

    const formData = {
        'form_id': data?.additionalData?.characteristic || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2)),
        'form_name': getConversionName(conversionType),
        'form_category': 'primary',
        'form_cta': formCta,
        'form_step': String(step),
        'form_step_name': getFormStepName(step, steps, lastStep),
        'form_step_action': getFormStepAction(conversionType),
        'form_type': 'overlay',
        'client_email': data?.form?.email || getStoredInputValues().email || '',
        'form_subject_type': '',
    };

    const product = getLastSeenProductItem();
    if (product?.item_id) {
        formData.ecommerce = {
            items: [{
                item_id: product.item_id,
                item_name: product.item_name || null,
                item_brand: product.item_brand || null,
                item_category: product.item_category || null,
                item_category2: product.item_category2 || null,
                item_variant: product.item_variant || null,
                item_discount: product.item_discount || 0,
                price: product.price || 0,
                quantity: product.quantity || 1,
                currency: product.currency || 'EUR',
            }]
        };
    }

    return formData;
};

export const pushVirtualPageView = (conversionType, step, layerData = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'event': 'virtualPageview',
        'virtualURL': `${getConversionName(conversionType)}/step_${step}`,
        'virtualTitle': `${getConversionName(conversionType)} step ${step}`,
        ...layerData,
    });
}
